import type { AuthUser } from '@lista/shared-validation';
import { create } from 'zustand';

const USER_KEY = 'lista.user';

/**
 * Who was signed in on this device (no tokens) - lets the app open read-only from the
 * service-worker cache when it starts offline. Cleared on sign-out.
 */
export function rememberedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}
function remember(user: AuthUser | null) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* storage unavailable */
  }
}

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

interface SessionState {
  status: SessionStatus;
  /** Access token lives in memory only - never in localStorage (security.md). Null while offline. */
  accessToken: string | null;
  user: AuthUser | null;
  signIn(accessToken: string | null, user: AuthUser): void;
  setAccessToken(accessToken: string): void;
  setUser(user: AuthUser): void;
  signOut(): void;
}

/** Authentication state - the only global client store besides UI toasts. */
export const useSession = create<SessionState>((set) => ({
  status: 'unknown',
  accessToken: null,
  user: null,
  signIn: (accessToken, user) => {
    remember(user);
    set({ status: 'authenticated', accessToken, user });
  },
  setAccessToken: (accessToken) => set({ accessToken }),
  setUser: (user) => {
    remember(user);
    set({ user });
  },
  signOut: () => {
    remember(null);
    set({ status: 'anonymous', accessToken: null, user: null });
  },
}));

export const getAccessToken = () => useSession.getState().accessToken;
